import { Card, CardContent, FormControlLabel, FormGroup, makeStyles } from '@material-ui/core';
import Switch from '@material-ui/core/Switch';
import React, { useEffect, useRef, useState } from 'react';

import { TextField } from 'src/common/ui/components';
import { PrimaryButton } from 'src/components/general/PrimaryButton';
import { SecondaryButton } from 'src/components/general/SecondaryButton';
import Snackbar from 'src/components/general/Snackbar';
import { Staff_PopInClinicSettingDataType as PopInClinicSettingDataType } from 'src/generated/gql/graphql';
import { usePopInClinicSettings } from 'src/popInClinic/settings/usePopInClinicSettings';

const booleanSettingLabels: Record<string, string> = {
  intakeQueueOpen: 'Intake queue open',
  followUpQueueOpen: 'Follow-up queue open',
  reengagementQueueOpen: 'Reengagement queue open',
  peerQueueOpen: 'Peer queue open',
  newPatientPrioritization: 'Prioritize new patients',
};
const booleanSettingNames = Object.keys(booleanSettingLabels);

const numberSettingLabels: Record<string, string> = {
  maxMaIntakeWait: 'Max MA intake wait',
};
const numberSettingNames = Object.keys(numberSettingLabels);

type LocalValues = Record<string, boolean | number>;

function buildLocalValues(
  settings: Partial<Record<string, { dataType: string; value: unknown }>>,
): LocalValues {
  const booleans = booleanSettingNames.map(name => [name, Boolean(settings[name]?.value)]);
  const numbers = numberSettingNames.map(name => [name, Number(settings[name]?.value ?? 0)]);
  return Object.fromEntries([...booleans, ...numbers]);
}

export const PopInClinicSettings: React.FC = () => {
  const classes = useStyles();
  const { clearSaveError, hasError, isLoading, isSaving, saveError, settings, updateSettings } =
    usePopInClinicSettings();
  const [localValues, setLocalValues] = useState<LocalValues>({});
  const [numberErrors, setNumberErrors] = useState<Record<string, string>>({});
  const initialized = useRef(false);
  // The values at load time; handleSave diffs against this so a setting changed on the
  // server after load isn't counted as a user edit.
  const initialValues = useRef<LocalValues>({});

  useEffect(() => {
    if (!isLoading && !initialized.current) {
      initialized.current = true;
      const initial = buildLocalValues(settings);
      initialValues.current = initial;
      setLocalValues(initial);
    }
  }, [isLoading, settings]);

  if (isLoading) {
    return <div>Fetching clinic settings</div>;
  }

  if (hasError) {
    return <div>Error retrieving clinic settings</div>;
  }

  const handleSave = async () => {
    // Save can fire on the render before the init effect seeds the baseline.
    if (!initialized.current) return;
    const changedBooleans = booleanSettingNames
      .filter(name => Boolean(localValues[name]) !== Boolean(initialValues.current[name]))
      .map(name => ({
        name,
        value: Boolean(localValues[name]),
        dataType: PopInClinicSettingDataType.Boolean,
      }));
    const changedNumbers = numberSettingNames
      .filter(name => Number(localValues[name]) !== Number(initialValues.current[name]))
      .map(name => ({
        name,
        value: Number(localValues[name]),
        dataType: PopInClinicSettingDataType.Number,
      }));
    const changed = [...changedBooleans, ...changedNumbers];
    if (changed.length === 0) return;
    const saved = await updateSettings(changed);
    // Only advance on success, so a failed save can be retried and Cancel still reverts.
    if (saved) initialValues.current = { ...localValues };
  };

  const handleCancel = () => {
    setLocalValues(initialValues.current);
    setNumberErrors({});
  };

  return (
    <div className={classes.root}>
      {saveError && (
        <Snackbar
          message="Failed to save clinic settings"
          color="error"
          onDismiss={clearSaveError}
        />
      )}
      <Card>
        <CardContent>
          <FormGroup>
            {Object.entries(booleanSettingLabels).map(([name, label]) => (
              <FormControlLabel
                key={name}
                control={
                  <Switch
                    name={name}
                    checked={Boolean(localValues[name])}
                    disabled={isSaving}
                    onChange={e => setLocalValues(prev => ({ ...prev, [name]: e.target.checked }))}
                  />
                }
                label={label}
              />
            ))}
            {Object.entries(numberSettingLabels).map(([name, label]) => (
              <FormControlLabel
                style={{ marginTop: 8, marginBottom: 8 }}
                key={name}
                control={
                  <div className={classes.numberInput}>
                    <TextField
                      name={name}
                      value={String(localValues[name] ?? '')}
                      disabled={isSaving}
                      error={!!numberErrors[name]}
                      onChange={e => {
                        const raw = e.target.value.replace(/[^0-9]/g, '');
                        const num = Number(raw);
                        setNumberErrors(prev => ({
                          ...prev,
                          [name]: raw !== '' && num > 400 ? 'Must be 400 or under' : '',
                        }));
                        setLocalValues(prev => ({ ...prev, [name]: raw === '' ? 0 : num }));
                      }}
                    />
                    {numberErrors[name] && (
                      <div className={classes.numberError}>{numberErrors[name]}</div>
                    )}
                  </div>
                }
                label={label}
              />
            ))}
          </FormGroup>
          <div className={classes.actions}>
            <SecondaryButton disabled={isSaving} onClick={handleCancel}>
              Cancel
            </SecondaryButton>
            <PrimaryButton
              disabled={isSaving || Object.values(numberErrors).some(Boolean)}
              onClick={handleSave}
            >
              Save
            </PrimaryButton>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

const useStyles = makeStyles(theme => ({
  root: {
    paddingTop: theme.spacing(3),
  },
  numberInput: {
    position: 'relative',
    width: 58,
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(1),
  },
  numberError: {
    position: 'absolute',
    top: '100%',
    color: theme.palette.error.main,
    fontSize: 12,
    whiteSpace: 'nowrap',
  },
  actions: {
    display: 'flex',
    gap: theme.spacing(1),
    marginTop: theme.spacing(2),
  },
}));
